import { state } from "./store";
import { setRoot, showError } from "./render";

declare global {
  interface Window { liff: any; }
}

const LIFF_ID = "2009825101-1s2yD67F";

async function init() {
  setRoot(`<div class="text-center mt-12 text-gray-500">起動中...</div>`);
  try {
    await window.liff.init({ liffId: LIFF_ID });
    if (!window.liff.isLoggedIn()) {
      window.liff.login();
      return;
    }

    // PC 版 LINE / 外部ブラウザでは IDToken が約 1 時間で切れる。期限切れなら logout + login で更新。
    // 30 秒のバッファを取って、もうすぐ切れるトークンも更新対象にする（admin SPA と同じ実装）。
    if (isIdTokenExpired()) {
      refreshLogin();
      return;
    }

    // Phase 1.5.3 ルーティング：?view=my でマイ予約一覧画面、それ以外は予約 wizard
    // LIFF はクエリ・パスを liff.state や URL search params で受け取る。
    // - 通常 URL: ?view=my
    // - LIFF 経由: ?liff.state=%2F%3Fview%3Dmy のように encode されて来るため両方を見る
    const view = resolveInitialView();
    if (view === "my") {
      const { renderMyReservations } = await import("./views/myReservations");
      await renderMyReservations();
      return;
    }

    // 既定：予約 wizard を Step 1 から開始
    const { renderStep1 } = await import("./steps/step1Date");
    renderStep1();
  } catch (e: any) {
    showError(`初期化エラー: ${e?.message ?? e}`);
  }
}

/**
 * LIFF IDToken の有効期限を確認する。
 * - getDecodedIDToken() が null/undefined → 取得不可とみなして expired 扱い
 * - exp（UNIX秒）が 30秒先より過去なら expired
 */
function isIdTokenExpired(): boolean {
  try {
    const decoded = window.liff.getDecodedIDToken();
    if (!decoded || typeof decoded.exp !== "number") return true;
    const nowSec = Date.now() / 1000;
    return decoded.exp < nowSec + 30;
  } catch {
    return true;
  }
}

/**
 * トークン期限切れ時に LINE で再ログインしてフレッシュなトークンを取得する。
 * logout で内部キャッシュを破棄してから login すると確実に新トークンが発行される。
 */
function refreshLogin(): void {
  try {
    window.liff.logout();
  } catch {
    // ignore
  }
  window.liff.login({ redirectUri: window.location.href });
}

/**
 * 初期表示する view を解決する。
 *
 * 優先順位：
 * 1. 通常 URL の ?view=my
 * 2. LIFF の ?liff.state=... 内の view=my
 * 3. デフォルト（予約 wizard）
 */
function resolveInitialView(): "my" | "wizard" {
  const params = new URLSearchParams(window.location.search);
  if (params.get("view") === "my") return "my";

  const liffState = params.get("liff.state");
  if (liffState) {
    try {
      const inner = new URLSearchParams(decodeURIComponent(liffState).replace(/^[?/]+/, ""));
      if (inner.get("view") === "my") return "my";
    } catch {
      // ignore
    }
  }

  return "wizard";
}

document.addEventListener("DOMContentLoaded", init);
